import * as Sentry from '@sentry/react';

const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const IGNORED_BROWSER_NOISE_PATTERNS = [
    /Failed to fetch dynamically imported module/i,
    /Importing a module script failed/i,
    /text\/html.+valid JavaScript MIME type/i,
    /Unable to preload CSS/i,
    /Invalid call to runtime\.sendMessage\(\)\. Tab not found/i,
    /Lock was stolen by another request/i,
];

type SentryEventLike = {
    message?: string;
    exception?: {
        values?: Array<{
            type?: string;
            value?: string;
            stacktrace?: {
                frames?: Array<{
                    filename?: string;
                    module?: string;
                    function?: string;
                }>;
            };
        }>;
    };
};

type SentryHintLike = {
    originalException?: unknown;
};

function getOriginalExceptionMessage(originalException: unknown): string | null {
    if (originalException instanceof Error) {
        return originalException.message;
    }
    if (typeof originalException === 'string') {
        return originalException;
    }
    return null;
}

function hasApplicationFrame(event: SentryEventLike): boolean {
    const frames = (event.exception?.values || []).flatMap((value) => value.stacktrace?.frames || []);
    return frames.some((frame) => {
        const filename = frame.filename || '';
        const moduleName = frame.module || '';
        return (
            filename.includes('/src/')
            || filename.includes('../../src/')
            || moduleName.startsWith('src.')
        );
    });
}

function isExternalReactRemoveChildNoise(event: SentryEventLike, messages: string[]): boolean {
    const isRemoveChildError = messages.some((message) => (
        /Failed to execute 'removeChild' on 'Node'/i.test(message)
    ));
    return isRemoveChildError && !hasApplicationFrame(event);
}

export function shouldIgnoreSentryEvent(event: SentryEventLike, hint?: SentryHintLike): boolean {
    const messages = [
        event.message,
        ...(event.exception?.values || []).flatMap((value) => [value.type, value.value]),
        hint ? getOriginalExceptionMessage(hint.originalException) : null,
    ].filter((message): message is string => Boolean(message));

    if (isExternalReactRemoveChildNoise(event, messages)) {
        return true;
    }

    return messages.some((message) => (
        IGNORED_BROWSER_NOISE_PATTERNS.some((pattern) => pattern.test(message))
    ));
}

/**
 * Initialize Sentry error reporting. No-op without VITE_SENTRY_DSN, so local
 * dev and tests never talk to Sentry. Default integrations capture uncaught
 * window errors and unhandled promise rejections.
 */
export function initErrorReporting(): void {
    if (!dsn) return;
    Sentry.init({
        dsn,
        environment: (import.meta.env.VITE_VERCEL_ENV as string | undefined) || import.meta.env.MODE,
        release: (import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA as string | undefined) || undefined,
        sendDefaultPii: false,
        beforeSend: (event, hint) => shouldIgnoreSentryEvent(event, hint) ? null : event,
    });
}

/** Report a handled error (e.g. from an error boundary). No-op without DSN. */
export function reportError(error: unknown, context?: Record<string, unknown>): void {
    if (!dsn) return;
    Sentry.captureException(error, context ? { extra: context } : undefined);
}
